import type { AnsweredQuestionsPart } from '@elara/domain/models/ChatMessageModelSpec';

import { cn } from '@elara/client-core/lib/utils';

import { slugify } from './widget-ids.js';

interface AnsweredQuestionsBlockProps {
  /**
   * Direct answers object - used by ChatInputView for pending answers
   * Values can be single string or array of strings for multi-select
   */
  answers?: Record<string, string | string[]>;
  /**
   * AnsweredQuestionsPart from message content - used by MessageContentRenderer
   */
  part?: AnsweredQuestionsPart;
  className?: string;
}

/**
 * Formats answer values for display.
 * Arrays are joined with commas.
 */
function formatAnswer(answer: string | string[]): string {
  return Array.isArray(answer) ? answer.join(', ') : answer;
}

/**
 * Displays answered questions in a compact format matching AskUserWidget style.
 * Can accept answers directly or extract them from an AnsweredQuestionsPart.
 *
 * Used in two contexts:
 * 1. ChatInputView - shows pending answers before sending
 * 2. MessageContentRenderer - shows answers in user messages
 */
export function AnsweredQuestionsBlock({ answers, part, className }: AnsweredQuestionsBlockProps) {
  const data = answers ?? part?.data.answeredQuestions ?? {};
  const entries = Object.entries(data);

  if (entries.length === 0) {
    return null;
  }

  return (
    <div className={cn('flex flex-col gap-4 my-4 last:mb-0', className)}>
      {entries.map(([question, answer]) => (
        <div key={slugify(question)} className="flex flex-col gap-1">
          <div className="text-sm text-muted-foreground">{question}</div>
          <div className="text-foreground">{formatAnswer(answer)}</div>
        </div>
      ))}
    </div>
  );
}
